import {
  Activity,
  Parent,
  SearchFilters as SearchFiltersType
} from "@outschool/gql-backend-generated";
import { DEFAULT_PARENT_NAME } from "@outschool/user";
import { TFunction } from "i18next";
import _, { capitalize } from "lodash";

import { totalClassesString } from "./Activity";
import * as SearchFilters from "./SearchFilters";
import {
  containsAgeRelatedTerm,
  containsClassRelatedTerm,
  containsPluralTerm
} from "./seo";

// TODO: add tests

export function getClassPageTitle(
  t: TFunction,
  activity: Pick<Activity, "age_max" | "age_min" | "isClub" | "title">
): string | null {
  if (!activity || !activity.title) {
    return null;
  }

  // Build and translate strings in a way that provides the most context.
  let activityDescription: string = "";
  const hasAgeRange = activity.age_min != null && activity.age_max != null;
  if (activity.isClub) {
    if (hasAgeRange) {
      activityDescription = t(
        "Moderated Online Community for Ages {{classAgeMin}}-{{classAgeMax}}",
        {
          classAgeMin: activity.age_min,
          classAgeMax: activity.age_max
        }
      );
    } else {
      activityDescription = t("Moderated Online Community");
    }
  } else {
    if (hasAgeRange) {
      activityDescription = t(
        "Small Online Class for Ages {{classAgeMin}}-{{classAgeMax}}",
        {
          classAgeMin: activity.age_min,
          classAgeMax: activity.age_max
        }
      );
    } else {
      activityDescription = t("Small Online Class");
    }
  }

  // TODO machine translate activity.title
  return `${activity.title} | ${activityDescription}`;
}

export const getDefaultPageTitle = (t: TFunction): string =>
  t(
    "Set Learning Free: Let kids' curiosity run wild with classes and groups on any topic you can imagine."
  );

export const getDefaultPageDescription = (t: TFunction) =>
  t(
    "Over {{totalClassesAtOutschool}} classes, endless possibilities. We empower kids 3 to 18 to build their own curriculum of interactive, one-of-a-kind classes.",
    { totalClassesAtOutschool: totalClassesString }
  );

export function getOnlineClassesPageTitle(
  term: string = "",
  titleCaseInput: boolean = true,
  inclusionStrategy: string = ""
): string {
  if (titleCaseInput) {
    term = toTitleCase(term).replace("For", "for");
  }
  if (inclusionStrategy === "facets-age") {
    if (!/^Classes/.test(term)) {
      return capitalize(term.replace(" for ", " classes for "));
    } else {
      return term;
    }
  } else if (inclusionStrategy === "facets-age-groups") {
    const ageRange = term.split(" ")[0];
    const category = term.replace(ageRange, "").trim();
    return `${category} classes for ${ageRange}`;
  } else if (containsClassRelatedTerm(term)) {
    return `${capitalizeFirstLetter(term)} for kids`;
  } else if (containsAgeRelatedTerm(term)) {
    return `${capitalizeFirstLetter(term)} classes`;
  } else if (containsPluralTerm(term)) {
    return `Classes about ${term}`;
  } else if (!_.isEmpty(term) || inclusionStrategy === "facets-popular") {
    return `${capitalizeFirstLetter(term)} classes for kids`;
  } else {
    return "Affordable online classes for kids: academic, tutoring, and more";
  }
}

export function getCategoryPillTitle(term: string = "") {
  return term.replace("For", "for");
}

export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.substr(1);
}

// TODO: abstract into SEO package
export function toTitleCase(str) {
  // using this instead of _.startCase because lodash removes special characters
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function getOnlineClassesPageDescription(inclusion_strategy?: string) {
  if (inclusion_strategy === "taught-in-spanish") {
    return "Discover immersive and engaging Spanish-language classes on Outschool, where your child can learn and improve their language skills while exploring a diverse range of educational topics taught by Spanish-speaking educators.";
  }
  return "Live online classes that make kids excited to learn. Try academic courses, virtual tutoring, fun clubs, and more. As low as $10 per class.";
}

export function getSearchPageTitle(
  t: TFunction,
  filters: SearchFiltersType
): string {
  if (_.isEmpty(filters)) {
    return t("Find 5-star online classes for kids at Outschool");
  } else if (filters.theme && _.isEmpty(_.omit(filters, "theme"))) {
    const themeMetaTitles = {
      Arts: t("Online Art Classes for K-12"),
      "Coding & Tech": t("Online Coding Classes for K-12"),
      English: t("Online English Classes for K-12"),
      "Health & Wellness": t("Online Health and Wellness Classes for K-12"),
      "Life Skills": t("Online Life Skills Classes for K-12"),
      Math: t("Online Math Classes for K-12"),
      Music: t("Online Music Classes for K-12"),
      "Science & Nature": t("Online Science Classes for K-12"),
      "Social Studies": t("Online Social Studies Classes for K-12"),
      "World Languages": t("Online Language Classes for K-12")
    };

    const themePageTitle = themeMetaTitles[filters.theme];
    return (
      themePageTitle ||
      t("{{classesSearchTheme}} | Online Classes for K-12", {
        classesSearchTheme: filters.theme
      })
    );
  } else {
    // TODO: translate summarySentence
    return SearchFilters.summarySentence(filters, true) + "";
  }
}

export function getSearchPageDescription(t: TFunction, theme: string): string {
  if (!theme) {
    return t(
      "Search our most popular online classes for kids. Find thousands of academic classes, tutoring, clubs, and more. Signup and set your kids up for success"
    );
  }

  const themeMetaDescriptions = {
    Arts: t(
      "Explore art through tea parties, and architecture history in Minecraft. Learn calligraphy, drawing, origami, acting, filmmaking, photography, and more!"
    ),
    "Coding & Tech": t(
      "Learn to use a computer, make a YouTube channel, game design, web design with HTML and CSS, programming languages like Java and Scratch and Python, and more!"
    ),
    English: t(
      "Learn and practice reading, study a novel, write a story, write an essay, get spelling and grammar help, and more!"
    ),
    "Health & Wellness": t(
      "Learn about nutrition, exercise, puberty, gratitude, social-emotional learning, and more!"
    ),
    "Life Skills": t(
      "Learn and practice logic, critical thinking, entrepreneurship, money management, career planning, and more!"
    ),
    Math: t(
      "Learn numbers, fractions, algebra, calculus, proofs, competitions, and more! Discover new math, reinforce fundamentals, or get extra help."
    ),
    Music: t(
      "Try singing or playing a new instrument, learn songwriting, composition, music theory, music history and more!"
    ),
    "Science & Nature": t(
      "Learn about ourselves and the world around us: the earth, marine and land animals, humans, climate, forensics, chemistry, physics and more!"
    ),
    "Social Studies": t(
      "Civics, world history, American history, geography, psychology, religions, and more."
    ),
    "World Languages": t(
      "Learn a language and practice with fellow students! Spanish, French, Chinese, Italian, and more!"
    )
  };

  const listingsThemeDescriptionIntro = t(
    "We offer a variety of interactive, small-group classes for kids and teens."
  );

  const themeDescription = themeMetaDescriptions[theme];
  return themeDescription
    ? `${listingsThemeDescriptionIntro} ${themeDescription}`
    : listingsThemeDescriptionIntro;
}

export const getParentPageTitle = (
  parent: Pick<Parent, "publicName">
): string => (!parent ? "" : parent.publicName ?? DEFAULT_PARENT_NAME);

export const getParentPageDescription = (
  t: TFunction,
  parent: Pick<Parent, "publicName" | "introduction">
): string =>
  !parent
    ? ""
    : parent.introduction
    ? // TODO translate parent.introduction
      _.truncate(parent.introduction, { length: 280, separator: /,? +/ })
    : t(
        "{{parentName}} is on Outschool to discover our small-group online classes for kids. Join us!",
        { parentName: parent.publicName }
      );
